import { Controller } from "@hotwired/stimulus"

// Drag-to-resize for a table's sticky first column.
//
// Only that column: it's the one whose width constrains reading, since every metric column scrolls
// horizontally past it. The width is written as a custom property on this wrapper (the CSS applies it
// to the header and body cells together) and remembered per surface in localStorage, so a reader who
// widens the campaign column keeps it on their next visit.
export default class extends Controller {
  static targets = ["handle"]
  static values = { storageKey: String }

  // Below MIN the column stops being readable; above MAX it crowds out the metrics it exists to be
  // read against.
  static MIN = 140
  static MAX = 720

  connect() {
    const remembered = this.remembered()
    if (remembered) this.applyWidth(remembered)
  }

  start(event) {
    event.preventDefault() // a drag from a <th> would otherwise start a text selection
    this.startX = event.clientX
    this.startWidth = this.stickyHeader?.offsetWidth
    if (!this.startWidth) return

    this.handleTarget.classList.add("is-dragging")
    // Capture the pointer so a release outside the viewport still delivers pointerup here — without
    // it the drag never ends and the column keeps tracking the cursor.
    this.handleTarget.setPointerCapture?.(event.pointerId)
    this.onMove = this.move.bind(this)
    this.onUp = this.end.bind(this)
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp, { once: true })
  }

  move(event) {
    this.applyWidth(this.clamp(this.startWidth + (event.clientX - this.startX)))
  }

  end() {
    window.removeEventListener("pointermove", this.onMove)
    // A Turbo navigation mid-drag detaches the table before pointerup lands. Measuring then reads 0,
    // which clamps to MIN and persists it — the column would come back stuck at its narrowest on
    // every future visit to this report.
    if (!this.element.isConnected) return

    this.handleTarget.classList.remove("is-dragging")
    this.remember(this.stickyHeader.offsetWidth)
  }

  disconnect() {
    if (this.onMove) window.removeEventListener("pointermove", this.onMove)
  }

  // --- internals ---

  get stickyHeader() {
    return this.element.querySelector("th.sticky-col")
  }

  applyWidth(px) {
    this.element.style.setProperty("--sticky-col-w", `${px}px`)
  }

  clamp(px) {
    return Math.min(Math.max(px, this.constructor.MIN), this.constructor.MAX)
  }

  storageName() {
    return this.storageKeyValue ? `v2_table_sticky_w:${this.storageKeyValue}` : null
  }

  remembered() {
    const name = this.storageName()
    if (!name) return null

    // localStorage throws in private-mode Safari and when storage is disabled; a remembered width is
    // a convenience, so never let it take the table down with it.
    try {
      const stored = parseInt(window.localStorage.getItem(name), 10)
      return Number.isFinite(stored) ? this.clamp(stored) : null
    } catch {
      return null
    }
  }

  remember(px) {
    const name = this.storageName()
    if (!name) return

    try {
      window.localStorage.setItem(name, String(this.clamp(px)))
    } catch {
      // ignore — see #remembered
    }
  }
}
